/**
 * FieldPathPicker Component
 *
 * A multi-select picker for workflow content field paths.
 * Used in ContentOverridesEditor to select which fields to hide.
 */

import React, { useState, useMemo } from 'react';
import { ALL_WORKFLOW_CONTENT_FIELD_PATHS, WORKFLOW_CONTENT_FIELD_PATHS } from '@/app/types/rewards';

interface FieldPathPickerProps {
  /** Currently selected field paths */
  selectedPaths: string[];
  /** Callback when selection changes */
  onChange: (paths: string[]) => void;
  /** Optional label for the picker */
  label?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Single select mode - closes dropdown after selection and only allows one item */
  singleSelect?: boolean;
}

interface FieldGroup {
  label: string;
  paths: readonly string[];
}

const FIELD_GROUPS: FieldGroup[] = [
  { label: 'Info Sheet Page', paths: WORKFLOW_CONTENT_FIELD_PATHS.infoSheet },
  { label: 'Pre-Redemption Page', paths: WORKFLOW_CONTENT_FIELD_PATHS.preRedemption },
  { label: 'Amount Selection Page', paths: WORKFLOW_CONTENT_FIELD_PATHS.amountSelection },
  { label: 'Success Page', paths: WORKFLOW_CONTENT_FIELD_PATHS.success },
];

/**
 * Format a field path for display (e.g., 'forms.amount_label' -> 'Amount Label')
 */
function formatFieldPath(path: string): string {
  // Get the last part of the path and convert to readable format
  const parts = path.split('.');
  const lastPart = parts[parts.length - 1] || path;
  return lastPart
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const FieldPathPicker: React.FC<FieldPathPickerProps> = ({
  selectedPaths,
  onChange,
  label = 'Select Fields',
  placeholder = 'Click to select fields to hide...',
  singleSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter paths based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return FIELD_GROUPS;
    
    const term = searchTerm.toLowerCase();
    return FIELD_GROUPS.map((group) => ({
      ...group,
      paths: group.paths.filter(
        (path) =>
          path.toLowerCase().includes(term) ||
          formatFieldPath(path).toLowerCase().includes(term)
      ),
    })).filter((group) => group.paths.length > 0);
  }, [searchTerm]);

  const togglePath = (path: string) => {
    if (singleSelect) {
      // Single select mode: replace selection and close dropdown
      onChange([path]);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      // Multi select mode: toggle selection
      if (selectedPaths.includes(path)) {
        onChange(selectedPaths.filter((p) => p !== path));
      } else {
        onChange([...selectedPaths, path]);
      }
    }
  };

  const selectAll = () => {
    onChange([...ALL_WORKFLOW_CONTENT_FIELD_PATHS]);
  };

  const clearAll = () => {
    onChange([]);
  };

  const toggleGroup = (paths: readonly string[]) => {
    const allSelected = paths.every((p) => selectedPaths.includes(p));
    if (allSelected) {
      onChange(selectedPaths.filter((p) => !paths.includes(p)));
    } else {
      const newPaths = [...selectedPaths];
      paths.forEach((p) => {
        if (!newPaths.includes(p)) {
          newPaths.push(p);
        }
      });
      onChange(newPaths);
    }
  };

  return (
    <div className="field-path-picker">
      {label && <label className="picker-label">{label}</label>}

      {/* Selected items display */}
      <div className="picker-trigger" onClick={() => setIsOpen(!isOpen)}>
        {selectedPaths.length === 0 ? (
          <span className="placeholder">{placeholder}</span>
        ) : (
          <div className="selected-items">
            {selectedPaths.slice(0, 3).map((path) => (
              <span key={path} className="selected-tag">
                {formatFieldPath(path)}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (singleSelect) {
                      // In single select mode, clear selection and open dropdown to pick a new one
                      onChange([]);
                      setIsOpen(true);
                    } else {
                      // In multi-select mode, remove this specific path
                      togglePath(path);
                    }
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {selectedPaths.length > 3 && (
              <span className="more-count">+{selectedPaths.length - 3} more</span>
            )}
          </div>
        )}
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="picker-dropdown">
          {/* Search */}
          <div className="picker-search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search fields..."
              autoFocus
            />
          </div>

          {/* Bulk actions (only in multi-select mode) */}
          {!singleSelect && (
            <div className="picker-actions">
              <button type="button" onClick={selectAll}>
                Select All
              </button>
              <button type="button" onClick={clearAll}>
                Clear All
              </button>
            </div>
          )}

          {/* Groups */}
          <div className="picker-groups">
            {filteredGroups.map((group) => {
              const allSelected = group.paths.every((p) => selectedPaths.includes(p));
              const someSelected =
                !allSelected && group.paths.some((p) => selectedPaths.includes(p));

              return (
                <div key={group.label} className="picker-group">
                  {!singleSelect && (
                    <div
                      className="group-header"
                      onClick={() => toggleGroup(group.paths)}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() => toggleGroup(group.paths)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="group-label">{group.label}</span>
                      <span className="group-count">
                        ({group.paths.filter((p) => selectedPaths.includes(p)).length}/
                        {group.paths.length})
                      </span>
                    </div>
                  )}
                  {singleSelect && (
                    <div className="group-header-simple">
                      <span className="group-label">{group.label}</span>
                    </div>
                  )}
                  <div className="group-items">
                    {group.paths.map((path) => (
                      <label key={path} className="path-item">
                        <input
                          type={singleSelect ? 'radio' : 'checkbox'}
                          checked={selectedPaths.includes(path)}
                          onChange={() => togglePath(path)}
                        />
                        <span className="path-name">{formatFieldPath(path)}</span>
                        <code className="path-value">{path}</code>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Close button */}
          <div className="picker-footer">
            <button type="button" className="done-btn" onClick={() => setIsOpen(false)}>
              Done ({selectedPaths.length} selected)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldPathPicker;

